/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Per-student due date extensions: Marin GET/POST helpers and save functions.
 * @author Satyaki Bose
 * @file StudentDueDatesAdjustmentUtils.js
 */

import Framework from '@greenville/framework';
import {
  AET_STORE_EVENTS,
  DUE_DATE_EXTENSION_EXTRA_DAYS_ORDERED,
  DUE_DATE_EXTENSION_OPTION_MESSAGE_KEY_PREFIX
} from '../common/Constants';

/**
 * Input: currentStudents (array of student objects), studentId, dueDateByAssignmentId (object {assignmentId: dueDate})
 * Patch assignmentDetails.dueDate from GET /assignments?studentId= (Marin V2).
 * @returns {{ updatedStudents: Array<Object> } | null}
 */
export function mergeAssignmentDueDatesIntoRosterStudents(currentStudents, studentId, dueDateByAssignmentId) {
  if (!studentId || !dueDateByAssignmentId || Object.keys(dueDateByAssignmentId).length === 0) {
    return null;
  }

  const idx = currentStudents.findIndex(s => s.studentId === studentId);
  if (idx === -1) {
    return null;
  }

  const student = currentStudents[idx];
  const patchedAssignmentDetails = (student.assignmentDetails || []).map((ad) => {
    const due = dueDateByAssignmentId[ad.assignmentId];
    return due === undefined ? ad : { ...ad, dueDate: due };
  });

  const updatedStudents = [...currentStudents];
  updatedStudents[idx] = {
    ...student,
    assignmentDetails: patchedAssignmentDetails
  };

  return { updatedStudents };
}

/** assignmentId -> due ISO from roster assignmentDetails (`dueDate` only). */
export function getStudentDueDatesRecordFromRosterStudent(student) {
  if (!student?.assignmentDetails?.length) {
    return {};
  }

  return Object.fromEntries(
    student.assignmentDetails
      .filter(ad => ad.assignmentId && ad.dueDate)
      .map(ad => [ad.assignmentId, ad.dueDate])
  );
}

/**
  * helper to get dropdown labels
  * any direct multiple of 7 will be shown as weeks
  */
function getDueDateExtensionOptionLabel(extraDays, getMessage) {
  if (extraDays > 0 && extraDays % 7 === 0) {
    const weeks = extraDays / 7;

    return getMessage(`${DUE_DATE_EXTENSION_OPTION_MESSAGE_KEY_PREFIX}.week.${weeks}`);
  }

  return getMessage(`${DUE_DATE_EXTENSION_OPTION_MESSAGE_KEY_PREFIX}.${extraDays}`);
}

// helper to build options for the dropdown UI
export function getDueDateAdjustmentSelectOptions(getMessage) {
  return DUE_DATE_EXTENSION_EXTRA_DAYS_ORDERED.map(extraDays => ({
    value: extraDays,
    label: getDueDateExtensionOptionLabel(extraDays, getMessage)
  }));
}

/** True when dueDate ISO moment is at or before now (time-inclusive). */
export function hasAssignmentDueDatePassed(dueDateIso, nowMs = Date.now()) {
  if (!dueDateIso) return false;
  const dueMs = new Date(dueDateIso).getTime();

  return !Number.isNaN(dueMs) && dueMs <= nowMs;
}

/** Resolve the due datetime shown in the student table for one row. */
export function getEffectiveDueDateIso(row, assignmentStore, studentSpecificDueDatesByAssignmentId) {
  const assignmentId = row.assignmentId || row.id;
  const courseDue = assignmentStore.getAssignmentById(assignmentId)?.dueDate;

  return studentSpecificDueDatesByAssignmentId[assignmentId] ?? courseDue ?? null;
}

/** divide roster table rows by whether their effective due date has passed. */
export function partitionAssignmentsByPastDue(tableRows = [], assignmentStore, studentSpecificDueDatesByAssignmentId) {
  return tableRows.reduce((acc, row) => {
    const dueDateIso = getEffectiveDueDateIso(row, assignmentStore, studentSpecificDueDatesByAssignmentId);
    const bucket = hasAssignmentDueDatePassed(dueDateIso) ? 'pastDueRows' : 'eligibleRows';
    acc[bucket].push(row);

    return acc;
  }, { pastDueRows: [], eligibleRows: [] });
}

// actual helper logic to extend the due date
function extendDueDateIso(dueIso, extraDays) {
  if (!dueIso || extraDays <= 0) return dueIso;

  const d = new Date(dueIso);
  // getTime of a valid iso must be a valid number like 1775569064725
  if (Number.isNaN(d.getTime())) return dueIso;

  // add the extra days to the date
  d.setUTCDate(d.getUTCDate() + extraDays);
  return Number.isNaN(d.getTime()) ? dueIso : d.toISOString();
}

// helper to build the assignments payload
function buildAssignmentsPayload(
  tableRows,
  assignmentStore,
  extraDays,
  studentSpecificDueDatesByAssignmentId = {}
) {
  const isDayOffset = DUE_DATE_EXTENSION_EXTRA_DAYS_ORDERED.includes(extraDays);
  // if not a day offset and no extra days, return empty array
  if (!isDayOffset && !extraDays) return [];

  return tableRows
    .map((row) => {
      const assignmentId = row.assignmentId || row.id;
      if (!assignmentId) return null;

      const courseAssignment = assignmentStore.getAssignmentById(assignmentId);
      const courseDue = courseAssignment?.dueDate;
      if (courseDue == null) return null;
      const dateAvailable = courseAssignment?.dateAvailable ?? '';
      // user chose last day of class, so offset is false, use extraDays
      if (!isDayOffset) {
        return { assignmentId, dueDate: extraDays, dateAvailable };
      }
      if (extraDays <= 0) {
        return { assignmentId, dueDate: courseDue, dateAvailable };
      }
      const effectiveDue = getEffectiveDueDateIso(row, assignmentStore, studentSpecificDueDatesByAssignmentId);

      return {
        assignmentId,
        dueDate: extendDueDateIso(effectiveDue, extraDays),
        dateAvailable
      };
    })
    .filter(Boolean);
}

// master helper to build payload
export function buildStudentSpecificDueDatePayload({
  studentId,
  courseId,
  extraDays,
  tableRows,
  assignmentStore,
  studentSpecificDueDatesByAssignmentId = {}
}) {
  return {
    studentId,
    courseId: courseId ?? null,
    extraDays,
    assignments: buildAssignmentsPayload(
      tableRows,
      assignmentStore,
      extraDays,
      studentSpecificDueDatesByAssignmentId
    )
  };
}

export function prepareStudentDueDateAdjustment({
  extraDays,
  tableRows,
  assignmentStore,
  studentSpecificDueDatesByAssignmentId,
  studentId,
  courseId
}) {
  const { pastDueRows, eligibleRows } = partitionAssignmentsByPastDue(
    tableRows,
    assignmentStore,
    studentSpecificDueDatesByAssignmentId
  );

  const allPastDue = eligibleRows.length === 0 && pastDueRows.length > 0;
  const skippedPastDueCount = pastDueRows.length;
  const updatedCount = eligibleRows.length;
  const request = buildStudentSpecificDueDatePayload({
    studentId,
    courseId,
    extraDays,
    tableRows: eligibleRows,
    assignmentStore,
    studentSpecificDueDatesByAssignmentId
  });

  return {
    allPastDue,
    skippedPastDueCount,
    updatedCount,
    request,
    shouldPost: !allPastDue && request.assignments.length > 0,
    immediateToast: allPastDue ? { type: 'allPastDue' } : null,
    postCompletionToast: skippedPastDueCount > 0
      ? { type: 'partial', updatedCount }
      : { type: 'success', updatedCount }
  };
}

// actual POST call
export function callToStudentSpecificDueDatesApi(request) {
  const { courseId, studentId, assignments } = request;

  if (!courseId || !studentId || !assignments?.length) {
    return Promise.resolve();
  }

  Framework.getEventManager().publish(AET_STORE_EVENTS.STUDENT_ASSIGNMENT_DUE_DATES_POST_REQUESTED, {
    courseId,
    studentId,
    assignments
  });

  return Promise.resolve();
}
